import Link16 from '@carbon/icons-react/es/link/16';
import moment from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import useClipboard from 'react-use-clipboard';
import { User } from 'api/models';
import { Button } from 'components/atoms/button';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { toastService } from 'components/molecules/toast';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { getUserLabel } from 'utils/get-user-label';
import { NewConversationAddMembersAction } from '../conversation-form';
import { Body, ChannelDetailsWrapper, Heading, Links } from './components';

interface Props {
  createdAt: number;
  createdBy: User;
  conversationId: number;
  conversationName: string;
  conversationDescription: string;
}

export const ChannelDetails: FC<Props> = ({
  createdBy,
  conversationName,
  createdAt,
  conversationDescription,
}) => {
  const [, copyToClipboard] = useClipboard(window.location.href);
  const message = useTranslation('copied');
  const createdByUser = useGetUserFromIndexedDb(createdBy?.userId);

  const description = useTranslation(
    'projects.conversations.copied_description',
  );
  const toast = toastService({
    title: message,
    message: description,
  });

  const { user } = useUser();

  return (
    <ChannelDetailsWrapper>
      <Heading>{conversationName}</Heading>
      <Body>
        {createdByUser ? (
          <FormattedMessage
            id="projects.conversations.details.start.with_creator"
            values={{
              sameUser: user?.userId === createdByUser?.userId,
              fromsame: (chunks) => (
                <span className="mention mention__self">{chunks}</span>
              ),
              fromother: (chunks) => <span className="mention">{chunks}</span>,
              user: getUserLabel(createdByUser),
              date: <HumanisedDate timestamp={moment(createdAt)} />,
            }}
          />
        ) : (
          <FormattedMessage
            id="projects.conversations.details.start.without_creator"
            values={{
              date: <HumanisedDate timestamp={moment(createdAt)} />,
            }}
          />
        )}
        <FormattedMessage
          id="projects.conversations.details.end"
          values={{
            conversationName,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
        {conversationDescription && (
          <FormattedMessage
            id="projects.conversations.details.description"
            values={{
              description: conversationDescription,
            }}
          />
        )}
      </Body>
      <Links>
        <NewConversationAddMembersAction conversationName={conversationName} />
        <Button
          size="small"
          type="link"
          iconPlacement="right"
          icon={<Link16 />}
          onClick={() => {
            copyToClipboard();
            toast.success();
          }}
        >
          <FormattedMessage id="projects.conversations.copy_link" />
        </Button>
      </Links>
    </ChannelDetailsWrapper>
  );
};
